import React, {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';

import { withViewportWidth } from 'decorators';
import { Card } from 'components/legacy/Card';
import { Paragraph } from 'components/core/Typography';
import AveragePricesHeader from './AveragePricesHeader';

import './AveragePricesContent.scss';

// ponytail: recharts is a ~200KB chunk; only fetch it once the graph is
// actually scrolled into view, so it doesn't count against page weight
// for visitors (and audits) that never scroll this far. Importing
// formatMonth from AveragePricesGraph here would pull recharts back into
// this chunk, so it's kept as a tiny local copy instead.
const AveragePricesGraph = lazy(() =>
	// ponytail: a rejected chunk load (CDN blip, stale hash after deploy)
	// falls back to the SSR summary instead of throwing with no error boundary
	import('./AveragePricesGraph').catch(() => ({
		default: function ChunkLoadFallback({
			priceHistory,
			onChunkLoadFailure,
		}) {
			// Tell the parent the real chart never mounted, so it stops
			// reserving space for a chart that is never coming.
			useEffect(() => {
				onChunkLoadFailure();
			}, [onChunkLoadFailure]);
			return <PriceRangeSummary priceHistory={priceHistory} />;
		},
	})),
);

export const enhance = withViewportWidth;

const formatMonth = (dateString) => {
	const [year, month] = dateString.split('-');
	const date = new Date(year, Number.parseInt(month, 10) - 1);
	return date.toLocaleDateString('en-US', {
		month: 'short',
		year: 'numeric',
	});
};

const isValidPricePoint = ({ price, date }) =>
	typeof price === 'number' && typeof date === 'string';

// SSR-able stand-in for the chart: real, crawlable text instead of an empty
// placeholder while the recharts chunk is deferred.
function PriceRangeSummary({ priceHistory }) {
	const validHistory = priceHistory.filter(isValidPricePoint);
	if (!validHistory.length) return null;

	const lowest = validHistory.reduce((a, b) => (b.price < a.price ? b : a));
	const highest = validHistory.reduce((a, b) => (b.price > a.price ? b : a));

	return (
		<Paragraph
			size="small"
			color="darkGray"
			last
		>
			{`Prices ranged from $${lowest.price.toFixed(2)} in ${formatMonth(lowest.date)} to $${highest.price.toFixed(2)} in ${formatMonth(highest.date)}.`}
		</Paragraph>
	);
}

PriceRangeSummary.propTypes = {
	priceHistory: PropTypes.arrayOf(
		PropTypes.shape({ date: PropTypes.string, price: PropTypes.number }),
	).isRequired,
};

export function AveragePricesContent({ city, data, viewportWidth }) {
	const priceHistory = get(data, 'priceHistory', []);
	const avgPrice = get(data, 'averagePrice', '');
	const priceDifference =
		avgPrice - get(priceHistory, `[${priceHistory.length - 1}].price`, 0);
	const hasChartablePriceHistory = priceHistory.some(isValidPricePoint);

	const graphContainerRef = useRef(null);
	const [showGraph, setShowGraph] = useState(false);
	const [chunkLoadFailed, setChunkLoadFailed] = useState(false);
	const [observerUnsupported, setObserverUnsupported] = useState(false);
	const handleChunkLoadFailure = useCallback(
		() => setChunkLoadFailed(true),
		[],
	);

	useEffect(() => {
		if (showGraph || !graphContainerRef.current) return undefined;
		if (typeof IntersectionObserver === 'undefined') {
			// SSR has no window, so this can only be learned after mount - start
			// optimistic (matches the SSR-rendered markup) and correct via state
			// once we learn otherwise, keeping the first client render isomorphic
			// with SSR instead of causing a hydration className mismatch.
			setObserverUnsupported(true);
			return undefined;
		}
		// AveragePricesGraph renders a fixed 400px chart regardless of data,
		// so never load it when there is nothing to plot - otherwise the
		// slot would jump from its unreserved height to 400px on scroll.
		if (!hasChartablePriceHistory) return undefined;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setShowGraph(true);
			},
			{ rootMargin: '200px' },
		);
		observer.observe(graphContainerRef.current);

		return () => observer.disconnect();
	}, [showGraph, hasChartablePriceHistory]);

	// Only reserve the chart's height while a chart could still arrive -
	// not when there is nothing to plot, the browser can't detect scroll,
	// or the chunk already failed to load. Otherwise the slot is left with
	// dead reserved space that will never be filled.
	const reservesGraphHeight =
		hasChartablePriceHistory && !observerUnsupported && !chunkLoadFailed;

	return (
		<Card className="average-prices-content">
			<AveragePricesHeader
				city={city}
				totalReservations={get(data, 'totalReservations', 0)}
				avgPrice={avgPrice}
				priceDifference={priceDifference}
				historyLength={priceHistory.length}
			/>
			<div
				ref={graphContainerRef}
				className={classnames('average-prices-graph-slot', {
					'average-prices-graph-slot--reserved': reservesGraphHeight,
				})}
			>
				{showGraph ? (
					<Suspense
						fallback={
							<PriceRangeSummary priceHistory={priceHistory} />
						}
					>
						<AveragePricesGraph
							priceHistory={priceHistory}
							avgPrice={avgPrice}
							viewportWidth={viewportWidth}
							onChunkLoadFailure={handleChunkLoadFailure}
						/>
					</Suspense>
				) : (
					<PriceRangeSummary priceHistory={priceHistory} />
				)}
			</div>
		</Card>
	);
}

AveragePricesContent.propTypes = {
	city: PropTypes.string.isRequired,
	data: PropTypes.shape({
		averageMonthlyUnitPrice: PropTypes.shape({
			average_price: PropTypes.string,
			price_history: PropTypes.arrayOf(
				PropTypes.shape({
					date: PropTypes.string,
					price: PropTypes.number,
				}),
			),
		}),
	}).isRequired,
	viewportWidth: PropTypes.number.isRequired,
};

export default enhance(AveragePricesContent);
